import MetaCRMTag from "@metacrm/metacrm-material-ui/dist/MetaCRMTag";
import { Box } from "@mui/material";
import { formatDate, formatWalletAgeDisplay } from "features/helpers/utils";
import { useFetchUserList } from "features/metadesk/redux/fetchUserList";
import useNavigateEntity from "hooks/useNavigateEntity";
import { cloneDeep, isObject, omit } from "lodash-es";
import { useMemo, useRef, useState } from "react";
import ViewModal from "./ViewModal";
import PopperMenu from "@metacrm/metacrm-material-ui/dist/PopperMenu";
import useDispatchSnackbar from "hooks/useDispatchSnackbar";
import { apiUrl } from "features/configure";
import { useOutletContext } from "react-router";
import axios from "axios";
import useGetPagePermission from "hooks/useGetPagePermission";
import { useIntl } from "react-intl";

const FilterList = ({
  isReport = false,
  hasFilter = false,
  currentView = "",
  handleCleanView = () => {},
  getViews = () => {},
  handleViewChange,
  selectedRowUserData,
  openFilterModal,
}) => {
  const { formatMessage } = useIntl();
  const { userListFilter: filter, updateUserListFilter } = useFetchUserList();
  const { navigateEntity } = useNavigateEntity();
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const dispatchSnackbar = useDispatchSnackbar();
  const anchorRef = useRef(null);
  const [viewOptionOpen, setViewOptionOpen] = useState(false);
  const [entityName] = useOutletContext();

  async function handleUpdateView() {
    try {
      await axios.put(
        `${apiUrl}/api/customerReport/${entityName}/view/${currentView?._id}`,
        {
          name: currentView?.name,
          filter: omit(filter, "selectedUser"),
        }
      );
      dispatchSnackbar({ message: "Update View Success", variant: "success" });
      getViews();
      setViewOptionOpen(false);
    } catch (err) {
      dispatchSnackbar({ err });
    }
  }

  async function handleDeleteFilterKey(key, value) {
    let newFilter = cloneDeep(filter);
    if (key === "tags") {
      const filteredTags = newFilter.tags.filter((tag) => tag._id !== value);
      if (filteredTags.length === 0) {
        newFilter = omit(newFilter, "tags");
      } else {
        newFilter = { ...newFilter, tags: filteredTags };
      }
    } else {
      newFilter = omit(filter, key); // Removes the key from the filter
    }
    const filterKeys = Object.keys(newFilter);
    const otherKeys = filterKeys.some((key) => {
      return key !== "page" && key !== "orderData" && key !== "search";
    });
    if (!otherKeys) {
      handleCleanView();
    }
    await updateUserListFilter(newFilter);
  }

  async function handleClearFilter(e) {
    e.stopPropagation();
    if (currentView) {
      handleCleanView();
    }
    updateUserListFilter(
      cloneDeep({
        page: 1,
        orderData: { sort: "walletTotalValue", order: -1 },
        search: "",
      })
    );
  }

  async function handleViewReport(e) {
    e.stopPropagation();
    if (selectedRowUserData.length > 0) {
      updateUserListFilter({
        ...filter,
        selectedUser: selectedRowUserData.map((user) => user._id),
      });
    } else {
      updateUserListFilter(omit(filter, "selectedUser"));
    }
    navigateEntity("/customers/analysis");
  }

  const showSaveView = useMemo(() => {
    const filterKeys = Object.keys(filter);
    return filterKeys.some((key) => {
      return (
        key !== "page" &&
        key !== "orderData" &&
        key !== "search" &&
        key !== "selectedUser"
      );
    });
  }, [filter]);

  const pagePermission = useGetPagePermission();
  const { canEdit } = pagePermission("customerReport");

  function validateFilter(value) {
    if (isObject(value) && value?.value === "") return false;
    if (Array.isArray(value) && value.length === 0) return false;
    return value;
  }

  if (!hasFilter) return <></>;
  return (
    <>
      <Box
        width="100%"
        bgcolor="rgba(123, 97, 255, 0.20)"
        p="12px 16px"
        justifyContent="space-between"
        display="flex"
        alignItems="center"
        fontSize={12}
        borderRadius="4px"
        mb="8px"
        sx={{ cursor: "pointer" }}
        onClick={openFilterModal}
      >
        <Box display="flex" alignItems="center">
          <Box
            sx={{ color: "#5643CC" }}
            fontSize={12}
            fontWeight={600}
            mr="20px"
            flexShrink={0}
          >
            {formatMessage({ id: "global.filterBy.title" })}
          </Box>
          <Box className="item-flex" gap="4px">
            {filter?.walletTotalValue && (
              <MetaCRMTag
                option={{
                  color: "rgba(255, 255, 255, 0.80)",
                  label: `${formatMessage({
                    id: "cdp.filters.walletTotalValue",
                  })} | $${filter?.walletTotalValue[0]}-$${
                    filter?.walletTotalValue[1]
                  }`,
                }}
                onDelete={() => handleDeleteFilterKey("walletTotalValue")}
              />
            )}
            {validateFilter(filter?.walletAge) && (
              <MetaCRMTag
                option={{
                  color: "rgba(255, 255, 255, 0.80)",
                  label: `${formatMessage({
                    id: "cdp.filters.walletAge",
                  })} | ${formatWalletAgeDisplay(
                    filter?.walletAge[0]
                  )}-${formatWalletAgeDisplay(filter?.walletAge[1])}`,
                }}
                onDelete={() => handleDeleteFilterKey("walletAge")}
              />
            )}
            {validateFilter(filter?.source) && (
              <MetaCRMTag
                option={{
                  color: "rgba(255, 255, 255, 0.80)",
                  label: `${formatMessage({ id: "cdp.filters.source" })} | ${
                    filter?.source?.label
                  }`,
                }}
                onDelete={() => handleDeleteFilterKey("source")}
              />
            )}
            {validateFilter(filter?.lastEngagementType) && (
              <MetaCRMTag
                option={{
                  color: "rgba(255, 255, 255, 0.80)",
                  label: `${formatMessage({
                    id: "cdp.filters.lastEngagementType",
                  })} | ${filter?.lastEngagementType?.label}`,
                }}
                onDelete={() => handleDeleteFilterKey("lastEngagementType")}
              />
            )}
            {validateFilter(filter?.lastEngagement) && (
              <MetaCRMTag
                option={{
                  color: "rgba(255, 255, 255, 0.80)",
                  label: `${formatMessage({
                    id: "cdp.filters.lastEngagementTimeRange",
                  })} | ${formatDate(filter?.lastEngagement[0], {
                    month: true,
                  })}-${formatDate(filter?.lastEngagement[1], {
                    month: true,
                  })}`,
                }}
                onDelete={() => handleDeleteFilterKey("lastEngagement")}
              />
            )}
            {validateFilter(filter?.tags) &&
              filter?.tags?.map((item) => (
                <MetaCRMTag
                  option={{
                    color: "rgba(255, 255, 255, 0.80)",
                    label: item.label,
                  }}
                  onDelete={() => handleDeleteFilterKey("tags", item._id)}
                />
              ))}
            {validateFilter(filter?.chainIds) && (
              <MetaCRMTag
                option={{
                  color: "rgba(255, 255, 255, 0.80)",
                  label: `${formatMessage({
                    id: "cdp.filters.activeChains",
                  })} | ${filter?.chainIds
                    .map((chain) => chain.name)
                    .join(" or ")}`,
                }}
                onDelete={() => handleDeleteFilterKey("chainIds")}
              />
            )}
            {validateFilter(filter?.ecosystems) && (
              <MetaCRMTag
                option={{
                  color: "rgba(255, 255, 255, 0.80)",
                  label: `${formatMessage({
                    id: "cdp.filters.ecosystems",
                  })} | ${filter?.ecosystems
                    .map((ecosystem) => ecosystem.name)
                    .join(" or ")}`,
                }}
                onDelete={() => handleDeleteFilterKey("ecosystems")}
              />
            )}
            {validateFilter(filter?.cexLabels) && (
              <MetaCRMTag
                option={{
                  color: "rgba(255, 255, 255, 0.80)",
                  label: `${formatMessage({
                    id: "cdp.filters.cex",
                  })} | ${filter?.cexLabels
                    .map((cexLabel) => cexLabel.value)
                    .join(" or ")}`,
                }}
                onDelete={() => handleDeleteFilterKey("cexLabels")}
              />
            )}

            {validateFilter(filter?.assets) && (
              <MetaCRMTag
                option={{
                  color: "rgba(255, 255, 255, 0.80)",
                  label: `${formatMessage({
                    id: "cdp.headline.holdings",
                  })} | ${filter?.assets
                    .map((asset) => asset.chainId.label)
                    .join(" or ")}`,
                }}
                onDelete={() => handleDeleteFilterKey("assets")}
              />
            )}

            {isReport && filter?.selectedUser && (
              <>
                <Box height="20px" bgcolor="#7B61FF" width="1px" mx="8px" />
                <MetaCRMTag
                  option={{
                    color: "rgba(255, 255, 255, 0.80)",
                    label: (
                      <Box className="item-flex" gap="4px">
                        <i
                          className="meta-crm-icon-ic_person font-size-16"
                          style={{ marginLeft: "-4px" }}
                        />
                        {`${filter?.selectedUser.length} Selected`}
                      </Box>
                    ),
                  }}
                  onDelete={() => handleDeleteFilterKey("selectedUser")}
                />
              </>
            )}
          </Box>
        </Box>

        <Box className="item-flex" flexShrink={0}>
          {!isReport && (
            <Box
              p="4px 8px"
              color="#7B61FF"
              className="item-flex"
              sx={{ cursor: "pointer" }}
              onClick={handleViewReport}
              fontWeight={700}
              id="btn_viewCustomerReport"
            >
              <i className="meta-crm-icon-ic_barChart font-size-12" />
              <Box ml="4px">
                {formatMessage({ id: "global.function.viewReport" })}
              </Box>
            </Box>
          )}
          {canEdit && isReport && showSaveView && (
            <>
              {currentView?.name !== "All items" ? (
                <Box
                  p="4px 8px"
                  color="#7B61FF"
                  className="item-flex"
                  sx={{ cursor: "pointer" }}
                  fontWeight={700}
                  ref={anchorRef}
                  onClick={(e) => {
                    e.stopPropagation();
                    setViewOptionOpen(true);
                  }}
                >
                  <i className="meta-crm-icon-ic_barChart font-size-12" />
                  <Box ml="4px">
                    {formatMessage({ id: "view.popup.saveView.title" })}
                  </Box>
                  <PopperMenu
                    open={viewOptionOpen}
                    onClose={() => setViewOptionOpen(false)}
                    anchorEl={anchorRef.current}
                    placement="bottom-end"
                    itemList={[
                      {
                        children: (
                          <div id="btn_saveCustomerAnalysisView">
                            {formatMessage({ id: "global.btn.saveChanges" })}
                          </div>
                        ),
                        func: () => handleUpdateView(),
                      },
                      {
                        children: (
                          <div id="btn_saveCustomerAnalysisView">
                            {formatMessage({ id: "global.btn.saveAsNewView" })}
                          </div>
                        ),
                        func: (e) => {
                          setViewModalOpen(true);
                        },
                      },
                    ]}
                  />
                </Box>
              ) : (
                <Box
                  p="4px 8px"
                  color="#7B61FF"
                  className="item-flex"
                  sx={{ cursor: "pointer" }}
                  onClick={(e) => {
                    e.stopPropagation();
                    setViewModalOpen(true);
                  }}
                  fontWeight={700}
                  id="btn_saveCustomerAnalysisView"
                >
                  <i className="meta-crm-icon-ic_barChart font-size-12" />
                  <Box ml="4px">
                    {" "}
                    {formatMessage({ id: "global.btn.saveAsNewView" })}
                  </Box>
                </Box>
              )}
            </>
          )}
          <Box
            p="4px 8px"
            color="#7B61FF"
            className="item-flex"
            sx={{ cursor: "pointer" }}
            onClick={handleClearFilter}
            fontWeight={700}
          >
            <i className="meta-crm-icon-ic_cancel font-size-12" />
            <Box ml="4px">{formatMessage({ id: "global.filter.clean" })}</Box>
          </Box>
        </Box>
      </Box>
      {viewModalOpen && (
        <ViewModal
          handleViewChange={handleViewChange}
          handleClose={() => setViewModalOpen(false)}
        />
      )}
    </>
  );
};

export default FilterList;
